import {
  Instance,
  applySnapshot,
  flow,
  getSnapshot,
  types,
} from "mobx-state-tree";
import api from "services/api";
import { reorder } from "utils/calculation";
import { MSURL } from "utils/ms";

const Workout = types.model("Workout", {
  id: types.integer,
  title: types.string,
  imageUrl: types.optional(types.string, ""),
  blobFileId: types.maybe(types.integer),
});

const WorkoutSequenceDay = types.model("WorkoutSequenceDay", {
  id: types.integer,
  order: types.integer,
  workout: Workout,
});

const WorkoutSequenceDayGroup = types.model("WorkoutSequenceDayGroup", {
  day: types.integer,
  workoutSequenceDays: types.array(WorkoutSequenceDay),
});

const DataWorkoutSequence = types.model("WorkoutSequence", {
  id: types.integer,
  title: types.string,
  showFirstWeekly: types.optional(types.boolean, false),
  days: types.array(WorkoutSequenceDayGroup),
});

const DataWorkoutSequenceList = types.model("WorkoutSequences", {
  id: types.integer,
  title: types.string,
  showFirstWeekly: types.optional(types.boolean, false),
});

const WorkoutSequenceResponse = types.model({
  success: types.boolean,
  message: types.string,
});

const WorkoutSequenceGetByIdResponse = types.model({
  success: types.boolean,
  data: DataWorkoutSequence,
  message: types.string,
});

const WorkoutSequenceGetListResponse = types.model({
  success: types.boolean,
  data: types.array(DataWorkoutSequenceList),
  message: types.string,
});

const WorkoutSequenceStore = types
  .model({
    sequencesList: types.array(DataWorkoutSequenceList),
    currentSequence: types.maybe(DataWorkoutSequence),
    isLoading: types.boolean,
  })
  .actions(self => ({
    createWorkoutSequence: flow(function*(
      WSTitle: string,
      showFirstWeekly: boolean,
    ) {
      try {
        const response = yield api.post(
          `/workouts/api/v1/workout-sequence`,
          { title: WSTitle, showFirstWeekly: !showFirstWeekly },
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error("Failed to create workout sequence");
        }
      } catch (error) {
        console.error(
          "An error occurred while creating workout sequence:",
          error,
        );
        throw error;
      }
    }),

    deleteWorkoutSequenceDay: flow(function*(WSId: number, WSDId: number) {
      self.isLoading = true;

      try {
        const response: Instance<typeof WorkoutSequenceResponse> = yield api.delete(
          `/workouts/api/v1/workout-sequence/${WSId}/workout-sequence-day/${WSDId}`,
          undefined,
          undefined,
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error(
            `Failed to delete workout-sequence-day ${WSDId} from sequ{WSDId} from sequence ${WSId}:`,
          );
        }
      } catch (error) {
        console.error(
          `An error occurred while updating workout sequence ${WSId}:`,
          error,
        );
        throw error;
      } finally {
        self.isLoading = false;
      }
    }),

    updateWorkoutSequence: flow(function*(
      WSId: number,
      WSTitle: string,
      showFirstWeekly: boolean,
    ) {
      try {
        const response = yield api.put(
          `/workouts/api/v1/workout-sequence/${WSId}`,
          { title: WSTitle, showFirstWeekly: !showFirstWeekly },
          undefined,
          MSURL,
        );
        if (!response.success) {
          console.error(`Failed to update workout sequence ${WSId}`);
        }
      } catch (error) {
        console.error(
          `An error occurred while updating workout sequence ${WSId}:`,
          error,
        );
        throw error;
      }
    }),

    fetchAllSequences: flow(function*() {
      self.isLoading = true;
      try {
        const response: Instance<typeof WorkoutSequenceGetListResponse> = yield api.get(
          "/workouts/api/v1/workout-sequence",
          undefined,
          undefined,
          MSURL,
        );
        if (response.success) {
          self.sequencesList = response.data || [];
        }
      } finally {
        self.isLoading = false;
      }
    }),

    fetchSequenceById: flow(function*(WSId: number) {
      self.isLoading = true;
      try {
        const response: Instance<typeof WorkoutSequenceGetByIdResponse> = yield api.get(
          `/workouts/api/v1/workout-sequence/${WSId}`,
          undefined,
          undefined,
          MSURL,
        );
        if (response.success) {
          self.currentSequence = response.data;
        }
      } finally {
        self.isLoading = false;
      }
    }),

    clearCurrentSequence() {
      self.currentSequence = undefined;
    },

    addWorkoutSequenceDay: flow(function*(WSId: number, payload: any) {
      try {
        const response = yield api.post(
          `/workouts/api/v1/workout-sequence/${WSId}/workout-sequence-day`,
          payload,
          undefined,
          MSURL,
        );

        if (!response.success) {
          console.error(`Failed to update workout sequence ${WSId}`);
        }
        return response;
      } catch (error) {
        console.error(
          `An error occurred while updating workout sequence ${WSId}:`,
          error,
        );
        throw error;
      }
    }),

    reorderWorkouts: flow(function*(
      sequenceId: number,
      day: number,
      payload: {
        id: number;
        afterId: number | undefined;
        moveType: string;
      },
    ) {
      self.isLoading = true;

      const prevSnapshot = getSnapshot(self.currentSequence);

      try {
        const dayObj = self.currentSequence?.days.find(d => d.day === day);
        if (dayObj) {
          const items = dayObj.workoutSequenceDays;
          const startIndex = items.findIndex(w => w.id === payload.id);
          let destIndex;

          if (payload.moveType === "Top") {
            destIndex = 0;
          } else if (payload.afterId) {
            destIndex = items.findIndex(w => w.id === payload.afterId) + 1;
          } else {
            destIndex = items.length - 1;
          }

          const reorderedItems = reorder(items.slice(), startIndex, destIndex);
          dayObj.workoutSequenceDays = reorderedItems;
        }

        const response: Instance<typeof WorkoutSequenceResponse> = yield api.post(
          `/workouts/api/v1/workout-sequence/${sequenceId}/workout-sequence-day/${day}/move`,
          payload,
          undefined,
          MSURL,
        );

        if (!response.success) {
          console.error(`Failed to move workout sequence day ${payload.id}`);
          applySnapshot(self.currentSequence, prevSnapshot);
        }

        return response;
      } catch (error) {
        console.error(
          `Error moving workout sequence day ${payload.id}:`,
          error,
        );

        applySnapshot(self.currentSequence, prevSnapshot);

        throw error;
      } finally {
        self.isLoading = false;
      }
    }),
  }));

export default WorkoutSequenceStore.create({
  sequencesList: [],
  currentSequence: undefined,
  isLoading: false,
});
